import type { MenteeBookingSlot } from './MenteePortalService';

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export type CalendarCell = {
    key: string;
    date: string | null;
    dayNum: number | null;
    inMonth: boolean;
    hasAvailable: boolean;
    hasBooked: boolean;
    isPastDay: boolean;
};

type Props = {
    year: number;
    month: number;
    slots: MenteeBookingSlot[];
    selectedDate: string | null;
    onPrev: () => void;
    onNext: () => void;
    onSelectDate: (date: string) => void;
};

const monthTitle = (year: number, month: number): string =>
    new Date(Date.UTC(year, month - 1, 1)).toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    });

/** Build Mon-first month grid; weekend cells never carry Mon–Fri slots. */
export const buildMonthCells = (
    year: number,
    month: number,
    slots: MenteeBookingSlot[]
): CalendarCell[] => {
    const byDate = new Map<string, MenteeBookingSlot[]>();
    for (const slot of slots) {
        const list = byDate.get(slot.date) || [];
        list.push(slot);
        byDate.set(slot.date, list);
    }
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const firstDowJs = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
    const firstDowMon = firstDowJs === 0 ? 6 : firstDowJs - 1;
    const cells: CalendarCell[] = [];
    for (let i = 0; i < firstDowMon; i += 1) {
        cells.push({
            key: `pad-${i}`,
            date: null,
            dayNum: null,
            inMonth: false,
            hasAvailable: false,
            hasBooked: false,
            isPastDay: false,
        });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
        const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const daySlots = byDate.get(date) || [];
        const hasAvailable = daySlots.some((s) => s.state === 'available');
        const hasBooked = daySlots.some((s) => s.state === 'booked');
        const isPastDay =
            daySlots.length > 0
                ? daySlots.every((s) => s.state === 'past' || s.state === 'booked') &&
                  !daySlots.some((s) => s.state === 'available') &&
                  daySlots.some((s) => s.state === 'past')
                : false;
        // Day with only booked (future) is still selectable to show booked pills.
        // Pure past (no available) is unbookable for new picks — still selectable to view.
        const pastOnly =
            daySlots.length > 0 && daySlots.every((s) => s.state === 'past');
        cells.push({
            key: date,
            date,
            dayNum: day,
            inMonth: true,
            hasAvailable,
            hasBooked,
            isPastDay: pastOnly || isPastDay,
        });
    }
    while (cells.length % 7 !== 0) {
        cells.push({
            key: `trail-${cells.length}`,
            date: null,
            dayNum: null,
            inMonth: false,
            hasAvailable: false,
            hasBooked: false,
            isPastDay: false,
        });
    }
    return cells;
};

/**
 * Month calendar for Book a Session (P18-BATCH-006).
 * Days with available slots are marked; past months/days remain navigable but unbookable.
 */
const MenteeSessionsBookCalendar = ({
    year,
    month,
    slots,
    selectedDate,
    onPrev,
    onNext,
    onSelectDate,
}: Props) => {
    const cells = buildMonthCells(year, month, slots);

    return (
        <div className="msess-cal-card">
            <div className="msess-cal-head">
                <h2 className="msess-cal-month">{monthTitle(year, month)}</h2>
                <div className="msess-cal-nav">
                    <button type="button" aria-label="Previous month" onClick={onPrev}>
                        ‹
                    </button>
                    <button type="button" aria-label="Next month" onClick={onNext}>
                        ›
                    </button>
                </div>
            </div>
            <p className="msess-tz">Times shown as Asia/Kolkata (IST) — naive wall-clock, no timezone conversion.</p>
            <div className="msess-cal-grid" role="grid" aria-label="Booking calendar">
                {DOW_LABELS.map((label) => (
                    <div key={label} className="msess-cal-dow">
                        {label}
                    </div>
                ))}
                {cells.map((cell) => {
                    if (!cell.inMonth || !cell.date) {
                        return <div key={cell.key} className="msess-cal-day" aria-hidden="true" />;
                    }
                    const daySlots = slots.filter((s) => s.date === cell.date);
                    const interactive = daySlots.length > 0;
                    const className = [
                        'msess-cal-day',
                        'is-in-month',
                        cell.hasAvailable ? 'is-has-slots' : '',
                        !cell.hasAvailable && cell.hasBooked ? 'is-has-booked' : '',
                        cell.isPastDay && !cell.hasAvailable ? 'is-past' : '',
                        selectedDate === cell.date ? 'is-selected' : '',
                    ]
                        .filter(Boolean)
                        .join(' ');
                    return (
                        <button
                            key={cell.key}
                            type="button"
                            className={className}
                            disabled={!interactive}
                            onClick={() => onSelectDate(cell.date as string)}
                        >
                            <span className="msess-cal-day-num">{cell.dayNum}</span>
                            {cell.hasAvailable ? (
                                <span className="msess-cal-dot" aria-hidden="true" />
                            ) : cell.hasBooked ? (
                                <span className="msess-cal-dot is-booked" aria-hidden="true" />
                            ) : null}
                        </button>
                    );
                })}
            </div>
            <div className="msess-legend" aria-hidden="true">
                <span className="msess-legend-avail">Available</span>
                <span className="msess-legend-booked">Booked (click hour to view)</span>
                <span className="msess-legend-past">Past (unbookable)</span>
            </div>
        </div>
    );
};

export default MenteeSessionsBookCalendar;
